import type { ButtonProps as ChakraButtonProps } from "@chakra-ui/react";
import { Button as BaseButton } from "@chakra-ui/react";
import {
  buttonPaintMap,
  buttonPaintMapActive,
  buttonPaintMapDisabled,
  buttonPaintMapHover,
  buttonPaintTextColor,
  type ButtonPaintMapKeyType,
} from "@shared/theme/props/buttonPaintMap";
import SanitizedChildren from "@ui-kit/other/SanitizedChildren/SanitizedChildren.web";
import React, { forwardRef, type ComponentProps } from "react";
import type { ButtonSharedProps } from "./Button.props";

/**
 * Refer to the UI component library on Figma for all possible button styles:
 * https://www.figma.com/design/G95FTO0TB7tT18qt35W0rL/Showpass-UI-Component-Library?node-id=2515-12084&node-type=canvas&t=OmuVtzm804Yz44Tj-0
 */

export type ButtonProps = ButtonSharedProps &
  Omit<
    ChakraButtonProps,
    keyof ButtonSharedProps | "disabled" | "loading" | "colorPalette"
  >;

// Get color styles for each variant using our thematic tokens
export function getColorStyles(
  variant: string | undefined,
  colorPalette: string | undefined,
  isLoading: boolean | undefined,
  color: string | undefined
): Record<string, unknown> {
  const buttonPaint = (colorPalette || "primary") as ButtonPaintMapKeyType;
  const paint = buttonPaintMap[buttonPaint] ? buttonPaint : "primary";

  switch (variant) {
    case "solid":
      return {
        bg: isLoading ? buttonPaintMapDisabled[paint] : buttonPaintMap[paint],
        color: color || buttonPaintTextColor[paint],
        _hover: {
          bg: buttonPaintMapHover[paint],
          _active: { bg: buttonPaintMapActive[paint] },
          _disabled: { bg: buttonPaintMapDisabled[paint] },
        },
        _active: { bg: buttonPaintMapActive[paint] },
        _disabled: { bg: buttonPaintMapDisabled[paint], opacity: 1 },
      };

    case "outline":
      return {
        bg: "transparent",
        borderColor: isLoading
          ? buttonPaintMapDisabled[paint]
          : buttonPaintMap[paint],
        color: color || buttonPaintMap[paint],
        _hover: {
          bg: buttonPaintMap[paint],
          color: buttonPaintTextColor[paint],
          _active: { bg: buttonPaintMapActive[paint] },
          _disabled: {
            borderColor: buttonPaintMapDisabled[paint],
            color: buttonPaintMapDisabled[paint],
          },
        },
        _active: {
          bg: buttonPaintMapActive[paint],
          color: buttonPaintTextColor[paint],
        },
        _disabled: {
          borderColor: buttonPaintMapDisabled[paint],
          color: buttonPaintMapDisabled[paint],
          opacity: 1,
        },
      };

    case "ghost":
      return {
        bg: "transparent",
        color: color || "fg",
        _hover: {
          _light: { bg: "gray.100" },
          _dark: { bg: "whiteAlpha.200" },
          _active: {
            _light: { bg: "gray.200" },
            _dark: { bg: "whiteAlpha.300" },
          },
          _disabled: {
            bg: "transparent",
            color: buttonPaintMapDisabled[paint],
          },
        },
        _active: {
          _light: { bg: "gray.200" },
          _dark: { bg: "whiteAlpha.300" },
        },
        _disabled: {
          bg: "transparent",
          color: buttonPaintMapDisabled[paint],
          opacity: 1,
        },
      };

    case "underline":
      return {
        opacity: 1,
        fontWeight: 700,
        textDecoration: "underline",
        textUnderlineOffset: "4px",
        bg: "transparent",
        color: color || "fg",
        _active: {
          bg: buttonPaintMapActive[paint],
          color: "fg",
        },
        _hover: {
          bg: buttonPaintMapHover[paint],
          color: "fg",
          _active: {
            bg: buttonPaintMapActive[paint],
          },
          _disabled: {
            opacity: 1,
            bg: "transparent",
            color: buttonPaintMapDisabled[paint],
          },
        },
        _disabled: {
          opacity: 1,
          bg: "transparent",
          color: buttonPaintMapDisabled[paint],
        },
      };

    case "link":
      return {
        bg: "transparent",
        color: color || buttonPaintMap[paint],
        textDecoration: "underline",
        _hover: {
          textDecoration: "none",
          color: buttonPaintMapHover[paint],
          _active: { color: buttonPaintMapActive[paint] },
          _disabled: { color: buttonPaintMapDisabled[paint] },
        },
        _active: { color: buttonPaintMapActive[paint] },
        _disabled: { color: buttonPaintMapDisabled[paint], opacity: 1 },
      };

    case "nav":
      return {
        bg: "transparent",
        color: color || "fg",
        fontWeight: 400,
        _hover: {
          _light: { bg: "gray.300" },
          _dark: { bg: "whiteAlpha.200" },
          _active: {
            _light: { bg: "gray.400" },
            _dark: { bg: "whiteAlpha.300" },
          },
          _disabled: {
            bg: "transparent",
            color: buttonPaintMapDisabled[paint],
          },
        },
        _active: {
          _light: { bg: "gray.400" },
          _dark: { bg: "whiteAlpha.300" },
        },
        _disabled: {
          bg: "transparent",
          color: buttonPaintMapDisabled[paint],
          opacity: 1,
        },
      };

    case "unstyled":
      return {
        bg: "transparent",
      };

    case "subtle":
    case "faded":
      return {
        bg: `${paint}.100`,
        color: color || `${paint}.700`,
        _hover: {
          bg: `${paint}.200`,
          _active: { bg: `${paint}.300` },
          _disabled: {
            bg: `${paint}.100`,
            color: `${paint}.300`,
          },
        },
        _active: { bg: `${paint}.300` },
        _disabled: {
          bg: `${paint}.100`,
          color: `${paint}.300`,
          opacity: 1,
        },
      };

    default:
      return {};
  }
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "solid",
      size = "md",
      isDisabled,
      disabled,
      isLoading,
      loading,
      colorPalette,
      isActive,
      leftIcon,
      rightIcon,
      color,
      ...props
    },
    ref
  ) => {
    // Map size names
    type ChakraButtonSize = ComponentProps<typeof BaseButton>["size"];
    let sizeValue: ChakraButtonSize | undefined;
    if (size === "small") sizeValue = "sm";
    else if (size === "medium") sizeValue = "md";
    else if (size === "large") sizeValue = "lg";
    else sizeValue = size;

    // Map size to fontSize (Chakra v3 may not include this by default)
    const fontSizeMap: Record<string, string> = {
      xs: "xs", // 12px
      sm: "sm", // 14px
      md: "md", // 16px
      lg: "lg", // 18px
    };
    const fontSize = fontSizeMap[sizeValue || "md"] || "md";

    // Map variant to Chakra's variant (for base styling: padding, layout)
    type ChakraVariant = ComponentProps<typeof BaseButton>["variant"];
    let chakraVariant: ChakraVariant = "solid";
    if (variant === "outline") chakraVariant = "outline";
    else if (variant === "ghost") chakraVariant = "ghost";
    else if (variant === "link" || variant === "nav" || variant === "unstyled")
      chakraVariant = "plain";

    const disabledValue = disabled !== undefined ? disabled : isDisabled;
    const loadingValue = loading !== undefined ? loading : isLoading;
    const colorPaletteValue = colorPalette ?? colorPalette ?? "primary";

    // Get our color overrides (coerce responsive values to strings)
    const variantString = typeof variant === "string" ? variant : "solid";
    const colorString = typeof color === "string" ? color : undefined;
    const colorStyles = getColorStyles(
      variantString,
      colorPaletteValue,
      loadingValue,
      colorString
    );

    // Add class for CSS targeting (used by desktop custom.css for Electron compatibility)
    const buttonClassName =
      variant === "outline" ? "chakra-button-outline" : undefined;

    return (
      <BaseButton
        ref={ref}
        variant={chakraVariant}
        size={sizeValue}
        fontSize={fontSize}
        disabled={disabledValue}
        loading={loadingValue}
        data-active={isActive ? "" : undefined}
        fontWeight={700}
        className={buttonClassName}
        {...colorStyles}
        {...props}
      >
        {leftIcon}
        <SanitizedChildren>{children}</SanitizedChildren>
        {rightIcon}
      </BaseButton>
    );
  }
) as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
  Solid: typeof Solid;
  Outline: typeof Outline;
  Link: typeof Link;
  Ghost: typeof Ghost;
  Nav: typeof Nav;
  Group: typeof Group;
};

Button.displayName = "Button";

const Solid = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} {...props} variant="solid" />
));
Solid.displayName = "Button.Solid";

const Outline = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} {...props} variant="outline" />
));
Outline.displayName = "Button.Outline";

const Link = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} {...props} variant="link" />
));
Link.displayName = "Button.Link";

const Ghost = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} {...props} variant="ghost" />
));
Ghost.displayName = "Button.Ghost";

const Nav = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} {...props} variant="nav" />
));
Nav.displayName = "Button.Nav";

const Group = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <Button ref={ref} {...props} variant="solid" />
));
Group.displayName = "Button.Group";

Button.Solid = Solid;
Button.Outline = Outline;
Button.Link = Link;
Button.Ghost = Ghost;
Button.Nav = Nav;
Button.Group = Group;

export default Button;
